import { Injectable, inject } from '@angular/core';
import { NavigationTarget } from '../navigation-target';
import { PortalUrls } from '../portal-urls.service';
import { ShellRoutes } from '../portal-routes';

/** Shell routes that restart the authentication flow and therefore are never a deeplink. */
const NON_RETURNABLE_ROUTES: readonly string[] = ['/public', ShellRoutes.landing, ShellRoutes.callback];

/**
 * Decides whether a requested URL is a safe deeplink. Only portal URLs are accepted,
 * which prevents the `returnUrl` parameter from being used as an open redirect.
 */
@Injectable({ providedIn: 'root' })
export class ReturnUrlPolicy {
  private readonly urls = inject(PortalUrls);

  toTarget(rawUrl: string): NavigationTarget | null {
    const url = this.parse(rawUrl);
    if (!url || !this.urls.isPortalUrl(url)) {
      return null;
    }
    if (url.origin !== this.urls.shellOrigin) {
      return NavigationTarget.external(url.href);
    }
    return this.isReturnable(url.pathname) ? NavigationTarget.internal(`${url.pathname}${url.search}${url.hash}`) : null;
  }

  private parse(rawUrl: string): URL | null {
    try {
      return new URL(rawUrl, this.urls.shellOrigin);
    } catch {
      return null;
    }
  }

  private isReturnable(pathname: string): boolean {
    return pathname !== '/' && !NON_RETURNABLE_ROUTES.some(route => pathname === route || pathname.startsWith(`${route}/`));
  }
}
